/**
 * Refund Request Modal
 * Modal to confirm refund request for a canceled ritual
 */

import { useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface RefundRequestModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  ritualTitle: string
  amount: string
  onConfirm: () => Promise<void>
  isLoading?: boolean
}

export function RefundRequestModal({
  isOpen,
  onOpenChange,
  ritualTitle,
  amount,
  onConfirm,
  isLoading = false,
}: RefundRequestModalProps) {
  const [isConfirming, setIsConfirming] = useState(false)

  const handleConfirm = async () => {
    setIsConfirming(true)
    try {
      await onConfirm()
      toast.success("Refund request submitted successfully")
      onOpenChange(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to submit refund request"
      toast.error(message)
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={true}
        className="w-lg bg-[#1A1917] border border-[#FFFFFF20] p-8 rounded-[40px_0_40px_0]"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-[#F2F0EB] text-xl font-semibold uppercase">
            Request Refund
          </DialogTitle>
          <DialogDescription className="text-[#F2F0EB80] text-sm leading-relaxed mt-2">
            You are requesting a refund for a canceled ritual. Our team will review your request and
            process it shortly.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 p-4 bg-[#FFFFFF08] rounded-xl border border-[#FFFFFF10]">
          <div className="flex justify-between items-center">
            <span className="text-[#F2F0EB80] text-sm">Ritual</span>
            <span className="text-[#F2F0EB] text-sm font-medium">{ritualTitle}</span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-[#F2F0EB80] text-sm">Refund Amount</span>
            <span className="text-[#F2F0EB] text-sm font-semibold">{amount}</span>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <div className="flex flex-col gap-3 w-full">
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={isConfirming || isLoading}
              className="flex-1 rounded-full py-4 uppercase text-sm font-semibold"
            >
              {isConfirming ? "Submitting..." : "Confirm Refund Request"}
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isConfirming || isLoading}
              className="flex-1 border border-[#FFFFFF40] text-[#F2F0EB] rounded-full py-4 uppercase text-sm font-semibold hover:bg-[#FFFFFF10]"
            >
              Cancel
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
